import React, { Children, isValidElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import PageHero from './PageHero';
import { usePageMeta } from './Seo';
import { useBrandName } from '../context/SettingsContext';

function slugify(text) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function extractTocItems(children) {
  return Children.toArray(children)
    .filter((child) => isValidElement(child) && child.type === LegalSection)
    .map((child) => ({
      id: child.props.id || slugify(child.props.title),
      title: child.props.title,
    }));
}

export { usePageMeta } from './Seo';

export default function LegalPageLayout({
  title,
  subtitle,
  badge = 'Informations légales',
  lastUpdated,
  metaDescription,
  path,
  children,
}) {
  const brandName = useBrandName();
  const tocItems = useMemo(() => extractTocItems(children), [children]);
  const description =
    metaDescription ||
    subtitle ||
    `Informations légales et réglementaires — ${title}`;

  usePageMeta({
    title: `${title} | ${brandName}`,
    description,
    path,
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero badge={badge} title={title} subtitle={subtitle} className="pb-10" />

      <div className="pb-16 sm:pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {lastUpdated && (
            <p className="text-sm text-slate-500 text-center mb-8 sm:mb-10">
              Dernière mise à jour :{' '}
              {new Date(lastUpdated).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          )}

          <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-14">
            {tocItems.length > 0 && (
              <aside className="hidden lg:block">
                <nav
                  aria-label="Table des matières"
                  className="sticky top-[calc(var(--site-header-offset)+1.5rem)] border-l-2 border-[rgba(58,0,224,0.15)] pl-5 py-1"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#3a00e0] mb-4">
                    Sommaire
                  </p>
                  <ol className="space-y-2.5 text-sm">
                    {tocItems.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="text-slate-600 hover:text-[#3a00e0] transition-colors leading-snug block"
                        >
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </aside>
            )}

            <div className="min-w-0">
              {tocItems.length > 0 && (
                <details className="lg:hidden mb-8 border border-slate-200 rounded-lg p-4 group">
                  <summary className="text-sm font-medium text-slate-900 cursor-pointer list-none flex items-center justify-between gap-3">
                    Sommaire
                    <ChevronDown
                      className="h-4 w-4 text-[#3a00e0] shrink-0 group-open:rotate-180 transition-transform"
                      aria-hidden="true"
                    />
                  </summary>
                  <ol className="mt-4 space-y-2 text-sm border-t border-slate-200 pt-4">
                    {tocItems.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          className="text-slate-600 hover:text-[#3a00e0] transition-colors"
                        >
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ol>
                </details>
              )}

              <article className="space-y-10 sm:space-y-12">{children}</article>

              <footer className="mt-12 sm:mt-14 pt-8 border-t border-slate-200 flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-500">
                <Link to="/mentions-legales" className="hover:text-[#3a00e0] transition-colors">
                  Mentions légales
                </Link>
                <span className="hidden sm:inline" aria-hidden="true">
                  ·
                </span>
                <Link to="/cgv" className="hover:text-[#3a00e0] transition-colors">
                  CGV
                </Link>
                <span className="hidden sm:inline" aria-hidden="true">
                  ·
                </span>
                <Link
                  to="/politique-confidentialite"
                  className="hover:text-[#3a00e0] transition-colors"
                >
                  Confidentialité
                </Link>
              </footer>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export function LegalSection({ title, id, children }) {
  const sectionId = id || slugify(title);

  return (
    <section id={sectionId} className="scroll-mt-site-header">
      <h2 className="text-xl font-semibold text-slate-900 mb-4 tracking-tight">
        {title}
      </h2>
      <div className="text-slate-600 leading-relaxed space-y-3 text-[15px] [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_strong]:text-slate-800 [&_a]:text-[#3a00e0] [&_a]:hover:underline [&_a]:underline-offset-2">
        {children}
      </div>
    </section>
  );
}
